import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { Search, ClipboardList, ChevronRight, X as XIcon } from 'lucide-react';
import { apiService, Appointment, Provider, Procedure, resolveLocalized } from '../../services/apiService';
import PageHeader from './ui/PageHeader';
import StatusPill, { StatusTone } from './ui/StatusPill';
import EmptyState from './ui/EmptyState';

const fmtDateDe = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(iso);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : iso;
};

const STATUS_META: Record<string, { label: string; tone: StatusTone }> = {
  pending: { label: 'Offen (Website)', tone: 'warning' },
  scheduled: { label: 'Geplant', tone: 'info' },
  confirmed: { label: 'Bestätigt', tone: 'success' },
  completed: { label: 'Abgeschlossen', tone: 'info' },
  cancelled: { label: 'Storniert', tone: 'danger' },
  no_show: { label: 'Nicht erschienen', tone: 'neutral' },
};

const inputClass =
  'w-full px-3 py-2 text-sm bg-brand-surface border border-brand-border text-brand-text rounded-md placeholder:text-brand-text-muted focus:outline-none focus:ring-2 focus:ring-brand-primary/40 focus:border-brand-primary/60 transition-colors';

const labelClass = 'block text-[11px] font-medium uppercase tracking-wider text-brand-text-muted mb-1';

const AppointmentsPage: React.FC = () => {
  const [appointments, setAppointments] = useState<Appointment[]>([]);
  const [providers, setProviders] = useState<Provider[]>([]);
  const [procedures, setProcedures] = useState<Procedure[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const [search, setSearch] = useState('');
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [providerFilter, setProviderFilter] = useState<string>('');
  const [procedureFilter, setProcedureFilter] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('');

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);
        const [a, p, r] = await Promise.all([
          apiService.getAppointments(),
          apiService.getProviders(false),
          apiService.getProcedures(false),
        ]);
        setAppointments(a.data.data || []);
        setProviders(p.data.data || []);
        setProcedures(r.data.data || []);
      } catch (e: any) {
        setError(e?.message || 'Daten konnten nicht geladen werden');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const filtered = useMemo(() => {
    return appointments
      .filter(a => {
        if (search && !`${a.patient_name} ${a.patient_email}`.toLowerCase().includes(search.toLowerCase())) return false;
        if (from && a.appointment_date < from) return false;
        if (to && a.appointment_date > to) return false;
        if (providerFilter && String(a.provider_id) !== providerFilter) return false;
        if (procedureFilter && String(a.procedure_id) !== procedureFilter) return false;
        if (statusFilter && a.status !== statusFilter) return false;
        return true;
      })
      .sort((x, y) =>
        `${x.appointment_date} ${x.start_time}`.localeCompare(`${y.appointment_date} ${y.start_time}`)
      );
  }, [appointments, search, from, to, providerFilter, procedureFilter, statusFilter]);

  const resetFilters = () => {
    setSearch(''); setFrom(''); setTo(''); setProviderFilter(''); setProcedureFilter(''); setStatusFilter('');
  };

  const hasActiveFilters = !!(search || from || to || providerFilter || procedureFilter || statusFilter);

  return (
    <div>
      <PageHeader
        eyebrow="Übersicht"
        title="Termine"
        description="Alle Termine durchsuchen und filtern"
        actions={
          <span className="text-xs text-brand-text-muted">
            <span className="font-semibold text-brand-text tabular-nums">{filtered.length}</span> von{' '}
            <span className="tabular-nums">{appointments.length}</span>
          </span>
        }
      />

      {error && (
        <div className="mb-4 rounded-lg bg-rose-50 dark:bg-rose-500/10 border border-rose-200 dark:border-rose-500/30 px-4 py-3 text-sm font-medium text-rose-800 dark:text-rose-300">
          {error}
        </div>
      )}

      {/* Filters */}
      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm p-4 mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          <div className="lg:col-span-3">
            <label className={labelClass}>Suche (Name oder E-Mail)</label>
            <div className="relative">
              <Search
                className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-brand-text-muted pointer-events-none"
                strokeWidth={1.75}
                aria-hidden
              />
              <input
                type="text"
                value={search}
                onChange={e => setSearch(e.target.value)}
                placeholder="z. B. Müller oder @example.com"
                className={`${inputClass} pl-9`}
              />
            </div>
          </div>
          <div>
            <label className={labelClass}>Von</label>
            <input type="date" value={from} onChange={e => setFrom(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Bis</label>
            <input type="date" value={to} onChange={e => setTo(e.target.value)} className={inputClass} />
          </div>
          <div>
            <label className={labelClass}>Status</label>
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className={inputClass}>
              <option value="">Alle</option>
              {Object.entries(STATUS_META).map(([k, v]) => (
                <option key={k} value={k}>{v.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className={labelClass}>Behandler</label>
            <select value={providerFilter} onChange={e => setProviderFilter(e.target.value)} className={inputClass}>
              <option value="">Alle</option>
              {providers.map(p => <option key={p.id} value={p.id}>{p.name}</option>)}
            </select>
          </div>
          <div>
            <label className={labelClass}>Behandlung</label>
            <select value={procedureFilter} onChange={e => setProcedureFilter(e.target.value)} className={inputClass}>
              <option value="">Alle</option>
              {procedures.map(p => <option key={p.id} value={p.id}>{resolveLocalized(p.name)}</option>)}
            </select>
          </div>
        </div>
        {hasActiveFilters && (
          <div className="mt-3 flex justify-end">
            <button
              onClick={resetFilters}
              className="inline-flex items-center gap-1 text-xs font-medium text-brand-primary hover:text-brand-primary-hover transition-colors cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-brand-primary/40 rounded-sm px-1"
            >
              <XIcon className="h-3 w-3" strokeWidth={2} />
              Filter zurücksetzen
            </button>
          </div>
        )}
      </div>

      {/* List */}
      <div className="rounded-xl bg-brand-surface border border-brand-border shadow-sm overflow-hidden">
        {loading ? (
          <div className="p-12 text-center text-sm text-brand-text-muted">Lade…</div>
        ) : filtered.length === 0 ? (
          <EmptyState
            icon={ClipboardList}
            title={hasActiveFilters ? 'Keine Treffer' : 'Noch keine Termine'}
            description={
              hasActiveFilters
                ? 'Keine Termine entsprechen den aktuellen Filtern.'
                : 'Sobald Termine vereinbart werden, erscheinen sie hier.'
            }
          />
        ) : (
          <>
            {/* Desktop: table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="min-w-full divide-y divide-brand-border">
                <thead className="bg-brand-surface-2/60">
                  <tr>
                    <Th>Datum / Zeit</Th>
                    <Th>Patient</Th>
                    <Th>Behandler</Th>
                    <Th>Behandlung</Th>
                    <Th>Raum</Th>
                    <Th>Status</Th>
                    <Th align="right"><span className="sr-only">Aktionen</span></Th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-brand-border">
                  {filtered.map(a => {
                    const meta = STATUS_META[a.status] ?? { label: a.status, tone: 'neutral' as StatusTone };
                    return (
                      <tr key={a.id} className="hover:bg-brand-surface-2/60 transition-colors">
                        <td className="px-4 py-3 text-sm whitespace-nowrap">
                          <div className="font-medium text-brand-text tabular-nums">{fmtDateDe(a.appointment_date)}</div>
                          <div className="text-xs text-brand-text-muted tabular-nums">{a.start_time}–{a.end_time}</div>
                        </td>
                        <td className="px-4 py-3 text-sm">
                          <div className="font-medium text-brand-text">{a.patient_name}</div>
                          <div className="text-xs text-brand-text-muted">{a.patient_email}</div>
                        </td>
                        <td className="px-4 py-3 text-sm text-brand-text">{a.provider_name}</td>
                        <td className="px-4 py-3 text-sm text-brand-text">{a.procedure_name}</td>
                        <td className="px-4 py-3 text-sm text-brand-text">{a.operatory_name}</td>
                        <td className="px-4 py-3 text-sm">
                          <StatusPill tone={meta.tone} dot>{meta.label}</StatusPill>
                        </td>
                        <td className="px-4 py-3 text-right text-sm">
                          <Link
                            to={`/admin/calendar?date=${a.appointment_date}&id=${a.id}`}
                            className="inline-flex items-center gap-1 text-brand-primary hover:text-brand-primary-hover hover:underline whitespace-nowrap font-medium cursor-pointer"
                          >
                            Im Kalender
                            <ChevronRight className="h-3.5 w-3.5" strokeWidth={2} aria-hidden />
                          </Link>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile: card list */}
            <ul className="md:hidden divide-y divide-brand-border">
              {filtered.map(a => {
                const meta = STATUS_META[a.status] ?? { label: a.status, tone: 'neutral' as StatusTone };
                return (
                  <li key={a.id}>
                    <Link
                      to={`/admin/calendar?date=${a.appointment_date}&id=${a.id}`}
                      className="block p-4 hover:bg-brand-surface-2/60 transition-colors cursor-pointer"
                    >
                      <div className="flex items-start justify-between gap-3">
                        <div className="min-w-0">
                          <div className="text-sm font-medium text-brand-text truncate">{a.patient_name}</div>
                          <div className="text-xs text-brand-text-muted tabular-nums">
                            {fmtDateDe(a.appointment_date)} · {a.start_time}–{a.end_time}
                          </div>
                          <div className="text-xs text-brand-text-muted mt-1">
                            {a.procedure_name} · {a.provider_name}
                          </div>
                        </div>
                        <StatusPill tone={meta.tone}>{meta.label}</StatusPill>
                      </div>
                    </Link>
                  </li>
                );
              })}
            </ul>
          </>
        )}
      </div>
    </div>
  );
};

const Th: React.FC<{ children: React.ReactNode; align?: 'left' | 'right' }> = ({ children, align = 'left' }) => (
  <th
    className={`px-4 py-3 text-${align} text-[11px] font-semibold uppercase tracking-wider text-brand-text-muted`}
  >
    {children}
  </th>
);

export default AppointmentsPage;
